import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { Divider, Input, Slider, Switch, Typography } from "antd";
import { THEME_COLOR } from "constants/style.constants";
import { debounce as _debounce } from "lodash";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";

import { ColorChooser, SimpleColorPicker } from "components/base";

const { Text } = Typography;

function SlopeSettings({
  id,
  slopeType,
  slope,
  label,
  showColorChooser,
  setShowColorChooser,
  customColors,
  setCustomColors
}) {
  const dispatch = useDispatch();
  const isActive = slope.active;
  const slopeStyle = slope.properties;

  const color = slopeStyle.colour || THEME_COLOR;
  const thickness = slopeStyle?.thickness || 3.0;
  const length = slopeStyle?.length || 50;

  const [isToggleActive, setIsToggleActive] = useState(isActive);
  const [localLength, setLocalLength] = useState(length);
  const [localThickness, setLocalThickness] = useState(thickness);

  const isColorChooserOpen = showColorChooser === slopeType;

  const updateShowSlope = useCallback(
    (isShowing) => {
      setIsToggleActive(isShowing);
      dispatch(
        updateChartByPath({
          id,
          path: `features.${slopeType}.active`,
          value: isShowing
        })
      );
    },
    [dispatch, id, slopeType]
  );

  const updateSlopeStyle = useCallback(
    (key) => (value) => {
      const next = {
        ...slopeStyle,
        [key]: value
      };
      dispatch(
        updateChartByPath({
          id,
          path: `features.${slopeType}.properties`,
          value: next
        })
      );
    },
    [dispatch, id, slopeStyle, slopeType]
  );

  const debouncedUpdateLength = useMemo(
    () => _debounce(updateSlopeStyle("length"), 300),
    [updateSlopeStyle]
  );

  const debouncedUpdateThickness = useMemo(
    () => _debounce(updateSlopeStyle("thickness"), 300),
    [updateSlopeStyle]
  );

  const handleLengthChange = useCallback(
    (e) => {
      let value = parseFloat(e.target.value);
      value = Math.max(0, Math.min(value, 100));
      setLocalLength(value);
      debouncedUpdateLength(value);
    },
    [debouncedUpdateLength]
  );

  const handleThicknessChange = useCallback(
    (value) => {
      setLocalThickness(value);
      debouncedUpdateThickness(value);
    },
    [debouncedUpdateThickness]
  );

  useEffect(() => {
    setLocalLength(length);
  }, [length]);

  useEffect(() => {
    setLocalThickness(thickness);
  }, [thickness]);

  useEffect(() => {
    return () => {
      debouncedUpdateLength.cancel();
      debouncedUpdateThickness.cancel();
    };
  }, [debouncedUpdateLength, debouncedUpdateThickness]);

  const handleColorPickerClick = useCallback(() => {
    setShowColorChooser(isColorChooserOpen ? "" : slopeType);
  }, [isColorChooserOpen, slopeType, setShowColorChooser]);

  return (
    <>
      <Head>
        <FlexItem>
          <SimpleColorPicker
            setColor={handleColorPickerClick}
            values={{
              name: slopeType,
              color: color,
              strokeColor: color,
              thickness: localThickness,
              opacity: null
            }}
          />
          <StyledText strong>{label}</StyledText>
        </FlexItem>
        <Switch size="small" checked={isToggleActive} onChange={updateShowSlope} />
      </Head>

      {isToggleActive && (
        <ScaleWrapper>
          <ScaleLabelWrapper>Length:</ScaleLabelWrapper>
          <ScaleInputWrapper>
            <Input
              value={localLength}
              type="number"
              step={1}
              id={`${slopeType}Length`}
              suffix="%"
              min={0}
              max={100}
              onChange={handleLengthChange}
            />
          </ScaleInputWrapper>
        </ScaleWrapper>
      )}

      {isColorChooserOpen && (
        <>
          <StyledDivider />
          <LinePreview color={color} thickness={localThickness} />
          <ColorChooser
            selectedColor={color}
            onChange={updateSlopeStyle("colour")}
            customColors={customColors}
            setCustomColors={setCustomColors}
          />
          <SliderWrapper>
            Thickness:
            <Slider
              min={1}
              max={20}
              value={localThickness}
              onChange={handleThicknessChange}
            />
          </SliderWrapper>
        </>
      )}
    </>
  );
}

function arePropsEqual(prevProps, nextProps) {
  const slopeChanged =
    JSON.stringify(prevProps.slope) !== JSON.stringify(nextProps.slope);
  const colorChooserForThisSlopeChanged =
    (prevProps.showColorChooser === prevProps.slopeType) !==
    (nextProps.showColorChooser === nextProps.slopeType);

  return !(slopeChanged || colorChooserForThisSlopeChanged);
}

export default memo(SlopeSettings, arePropsEqual);

const Head = styled.div`
  min-width: 220px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ScaleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 24px;
  gap: 4px;
`;

const ScaleInputWrapper = styled.div`
  width: 80%;
`;

const ScaleLabelWrapper = styled.div`
  margin-top: 5px;
`;

const StyledText = styled(Text)`
  text-transform: uppercase;
  padding-left: 2px;
  padding-top: 1px;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const LinePreview = styled.div`
  background-color: ${(p) => p.color};
  height: ${(p) => p.thickness}px;
  width: 80%;
  margin: 8px auto;
`;

const SliderWrapper = styled.div`
  display: grid;
`;

const FlexItem = styled.div`
  display: inline-flex;
  gap: 2px;
`;
